import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableCell from '@mui/material/TableCell';
import DialogConfirm from '../../../components/_dialog/DialogConfirm';
import useNotification from '../../../components/_hook/useNotification';
import { useUnpublishPlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/unpublishPlanAdministratorsMutation';
import StatusIconButton from '../../../components/_iconButton/StatusIconButton';
import { usePublishPlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/publishPlanAdministratorsMutation';
import DeleteIconButton from '../../../components/_iconButton/DeleteIconButton';
import { useDeletePlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/deletePlanAdministratorsMutation';
import { IUserForeignKeySchema } from '../../../../graphql/type/schemaForeignKey/user';
import SchemaUserName from '../../../components/_schema/SchemaUserName';
import { useSetSuperAdminPlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/setSuperAdminPlanAdministratorsMutation';
import { useUnsetSuperAdminPlanAdministratorsMutation } from '../../../../graphql/mutation/plan/planAdministrator/unsetSuperAdminPlanAdministratorsMutation';
import UserStatusIconButton from '../../../components/_iconButton/UserStatusIconButton';
import { ICompanySchema } from '../../../../graphql/type/company';
import SuperAdminPermission from '../../../adminComponents/_permission/SuperAdminPermission';
import useKeepitalRoutes from '../../../components/_hook/useKeepitalRoutes';
import ExternalLinkIconButton from '../../../components/_iconButton/ExternalLinkIconButton';
import { useSendAddPlanAdministratorEmailMutation } from '../../../../graphql/mutation/plan/planAdministrator/sendAddPlanAdministratorEmailMutation';
import EmailIconButton from '../../../components/_iconButton/EmailIconButton';
import Context from '../../../context';

interface IProps {
  item: IUserForeignKeySchema;
  keepitalTeams?: ICompanySchema['teams'];
  setTableRefreshKey?: (tableRefreshKey: number) => void;
}

export default function PlanAdministratorTableItem(props: IProps) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey, keepitalTeams } = props;
  const { notification } = useNotification();
  const { path: keepitalPath } = useKeepitalRoutes();
  const { company } = useContext(Context);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [
    deletePlanAdministratorsMutation,
    { loading: isDeletingPlanAdministrators },
  ] = useDeletePlanAdministratorsMutation(
    `
  updatedPlan {
      _id
    }`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___deleted', { item: `${item.id} ${t('administrator')}` })
        );
        if (setTableRefreshKey) {
          setTableRefreshKey(new Date().getTime());
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    publishPlanAdministratorsMutation,
    { loading: isPublishingPlanAdministrators },
  ] = usePublishPlanAdministratorsMutation(
    `updatedPlan {
				_id
				users {
				  id
                  status
                }
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishPlanAdministratorsMutation,
    { loading: isUnpublishingPlanAdministrators },
  ] = useUnpublishPlanAdministratorsMutation(
    `updatedPlan {
				_id
				users {
				  id
                  superAdmin
                }
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [
    setSuperAdminPlanAdministratorsMutation,
    { loading: isSettingSuperAdminPlanAdministrators },
  ] = useSetSuperAdminPlanAdministratorsMutation(
    `updatedPlan {
				_id
				users {
				  id
                  superAdmin
                }
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('this_person_will_be_the_superAdmin_of_this_company')
        );
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unsetSuperAdminPlanAdministratorsMutation,
    { loading: isUnsettingSuperAdminPlanAdministrators },
  ] = useUnsetSuperAdminPlanAdministratorsMutation(
    `updatedPlan {
				_id
				users {
				  id
                  status
                }
			}`,
    {
      onCompleted: () => {
        notification.warning(
          t('this_person_is_no_more_in_charge_person_of_this_company')
        );
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [
    sendAddPlanAdministratorEmailMutation,
    { loading: isSendingAddPlanAdministratorEmailEmail },
  ] = useSendAddPlanAdministratorEmailMutation('isSuccess', {
    onCompleted: () => {
      notification.success(t('success'));
    },
    onError: (e) => {
      notification.error(e.message);
    },
  });

  async function onClickSendSupportNotifyEmail() {
    if (item.id) {
      await sendAddPlanAdministratorEmailMutation({
        variables: {
          companyId: company._id,
          administratorId: item.id,
        },
      });
    }
  }

  async function onClickPublish() {
    if (item.id) {
      await publishPlanAdministratorsMutation({
        variables: {
          companyId: company._id,
          administratorIds: [item.id],
        },
      });
    }
  }

  async function onClickUnpublish() {
    if (item.id) {
      await unpublishPlanAdministratorsMutation({
        variables: {
          companyId: company._id,
          administratorIds: [item.id],
        },
      });
    }
  }

  async function onClickSetSuperAdmin() {
    if (item.id) {
      await setSuperAdminPlanAdministratorsMutation({
        variables: {
          companyId: company._id,
          administratorIds: [item.id],
        },
      });
    }
  }

  async function onClickUnsetSuperAdmin() {
    if (item.id) {
      await unsetSuperAdminPlanAdministratorsMutation({
        variables: {
          companyId: company._id,
          administratorIds: [item.id],
        },
      });
    }
  }

  async function onClickDelete() {
    if (item.id) {
      await deletePlanAdministratorsMutation({
        variables: {
          companyId: company._id,
          administratorIds: [item.id],
        },
      });
    }
  }

  return (
    <>
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete__item__', { item: item.id })}
        open={isOpenDeleteDialog}
        onClose={() => setIsOpenDeleteDialog(false)}
        onConfirm={onClickDelete}
      />
      <TableCell align="left">
        {item.userData && <SchemaUserName user={item.userData} useIndexLink />}
      </TableCell>
      <TableCell align="right">
        {keepitalTeams &&
          Boolean(
            keepitalTeams.find((keepitalTeam) => keepitalTeam.id === item.id)
          ) && (
            <UserStatusIconButton
              status={item.superAdmin || false}
              loading={
                isSettingSuperAdminPlanAdministrators ||
                isUnsettingSuperAdminPlanAdministrators
              }
              tooltip={`${t('keepital')} ${t('administrator')}`}
              onClick={
                item.superAdmin ? onClickUnsetSuperAdmin : onClickSetSuperAdmin
              }
            />
          )}
        <StatusIconButton
          status={item.status}
          loading={
            isUnpublishingPlanAdministrators || isPublishingPlanAdministrators
          }
          tooltip={item.status ? t('publish') : t('unpublish')}
          onClick={item.status ? onClickUnpublish : onClickPublish}
        />

        <DeleteIconButton
          tooltip={t('delete')}
          loading={isDeletingPlanAdministrators}
          onClick={() => setIsOpenDeleteDialog(true)}
        />
        <SuperAdminPermission>
          <ExternalLinkIconButton
            tooltip={`${t('user')} ${t('admin')} ${t('page')} (${t(
              'only_super_admin_will_see_this'
            )})`}
            href={keepitalPath('user.dashboard', { userID: item.id })}
            target="_blank"
          />
        </SuperAdminPermission>
        <SuperAdminPermission>
          <EmailIconButton
            loading={isSendingAddPlanAdministratorEmailEmail}
            tooltip={`${t('notify')} (${t('only_super_admin_will_see_this')})`}
            onClick={onClickSendSupportNotifyEmail}
          />
        </SuperAdminPermission>
      </TableCell>
    </>
  );
}
